import * as React from 'react';
// Import hooks.
import {
	useInputCurrency,
    UseInputCurrencyResults,
} from '@app/hooks/view/input';
// Import 
import {
    SubformBuildingLimitsProps,
	SubformInputSettingsBuildingLimitsProps,
} from '@app/props';
// Import api models.
import {
	BuildingLimitsCommercial,
} from '@app/models/api';

interface UseSubformBuildingLimitsParameters {
	id: string;
	allowEdits: boolean;
	buildingReplacementCostValue?: number;
	buildingLimitsCommercial?: BuildingLimitsCommercial;
	inputSettingsProps?: SubformInputSettingsBuildingLimitsProps;
	showValidationsOnMount?: boolean;
}

interface UseSubformBuildingLimitsResults {
	areAllInputsDisabled: boolean;
	buildingLimitInput: UseInputCurrencyResults;
	contentsLimitInput: UseInputCurrencyResults;
	businessIncomeExtraExpenseLimitInput: UseInputCurrencyResults;
	applySubformBuildingLimitsProps: () => SubformBuildingLimitsProps;
	createBuildingLimitsCommercial: () => BuildingLimitsCommercial;
	flagAllInputsToShowValidations: () => void;
	doInputsHaveChanges: () => boolean;
}

const useSubformBuildingLimits = (params: UseSubformBuildingLimitsParameters): UseSubformBuildingLimitsResults => {

	// #region exported methods

	const applySubformBuildingLimitsProps = (): SubformBuildingLimitsProps => {
		return {
			showLimits,
			showBuildingLimitInput,
			showContentsLimitInput,
			showBusinessIncomeExtraExpenseLimitInput,
			buildingLimitInput,
			contentsLimitInput,
			businessIncomeExtraExpenseLimitInput,
		};
	}

	/**
	 *  Creates a building risk data commercial object.
	 */
	const createBuildingLimitsCommercial = (): BuildingLimitsCommercial => {
		let buildingLimitsCommercial: BuildingLimitsCommercial = {
			building: buildingLimitInput.value ?? null,
			contents: contentsLimitInput.value ?? null,
			businessIncomeExtraExpense: businessIncomeExtraExpenseLimitInput.value ?? null,
		};

		return buildingLimitsCommercial;
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		buildingLimitInput.changeShowValidationFlag(true);
		contentsLimitInput.changeShowValidationFlag(true);
		businessIncomeExtraExpenseLimitInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doInputsHaveChanges = (): boolean => {
		return buildingLimitInput.doesInputHaveChanges
			|| contentsLimitInput.doesInputHaveChanges
			|| businessIncomeExtraExpenseLimitInput.doesInputHaveChanges;
	}

	// #endregion exported methods


	// #region private methods

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateBaseId = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the building limit. 
	 **/
	const generateIdForBuildingLimit = (): string => {
		return `${generateBaseId()}-buildingLimit`;
	}

	/**
	 * Generates id for the contents limit. 
	 **/
	const generateIdForContentsLimit = (): string => {
		return `${generateBaseId()}-contentsLimit`;
	}

	/**
	 * Generates id for the business income extra expense limit. 
	 **/
	const generateIdForBusinessIncomeExtraExpenseLimit = (): string => {
		return `${generateBaseId()}-businessIncomeExtraExpenseLimit`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const showValidation: boolean = React.useMemo(() => params.showValidationsOnMount ?? false, [params.showValidationsOnMount]);

	// #region Input settings visibility.

	const showBuildingLimitInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.buildingLimitInput !== 'hidden',
		[
			params.inputSettingsProps?.buildingLimitInput,
		]
	);
	const showContentsLimitInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.contentsLimitInput !== 'hidden',
		[
			params.inputSettingsProps?.contentsLimitInput,
		]
	);
	const showBusinessIncomeExtraExpenseLimitInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.businessIncomeExtraExpenseLimitInput !== 'hidden',
		[
			params.inputSettingsProps?.businessIncomeExtraExpenseLimitInput,
		]
	);

	// #endregion Input settings visibility.

	// #region Input settings disable.

	const isBuildingLimitInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showBuildingLimitInput
		|| params.inputSettingsProps?.buildingLimitInput === 'disabled',
		[
			params.allowEdits,
			showBuildingLimitInput,
			params.inputSettingsProps?.buildingLimitInput
		]
	);
	const isContentsLimitInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showContentsLimitInput
		|| params.inputSettingsProps?.contentsLimitInput === 'disabled',
		[
			params.allowEdits,
			showContentsLimitInput,
			params.inputSettingsProps?.contentsLimitInput
		]
	);
	const isBusinessIncomeExtraExpenseLimitInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showBusinessIncomeExtraExpenseLimitInput
		|| params.inputSettingsProps?.businessIncomeExtraExpenseLimitInput === 'disabled',
		[
			params.allowEdits,
			showBusinessIncomeExtraExpenseLimitInput,
			params.inputSettingsProps?.businessIncomeExtraExpenseLimitInput
		]
	);

	// #endregion Input settings disable


	// Setup the 'building limit' input hook.
	const buildingLimitInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForBuildingLimit(),
		label: 'Building Limit',
		validationLabel: 'building limit',
		required: true,
		disabled: isBuildingLimitInputDisabled,
		value: params.buildingLimitsCommercial?.building ?? 0,
		minValue: 0,
		maxValue: params.buildingReplacementCostValue ?? 0,
		maxValueInclusive: true,
		maxValueLabel: 'replacement cost value',
		showValidation: showValidation,
	});

	// Setup the 'contents limit' input hook.
	const contentsLimitInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForContentsLimit(),
		label: 'Contents Limit',
		validationLabel: 'contents limit',
		required: true,
		disabled: isContentsLimitInputDisabled,
		value: params.buildingLimitsCommercial?.contents ?? 0,
		minValue: 0,
		minValueInclusive: true,
		showValidation: showValidation,
	});

	// Setup the 'business income extra expense limit' input hook.
	const businessIncomeExtraExpenseLimitInput: UseInputCurrencyResults = useInputCurrency({
		id: generateIdForBusinessIncomeExtraExpenseLimit(),
		label: 'Business Income Extra Expense Limit',
		validationLabel: 'business income extra expense limit',
		required: true,
		disabled: isBusinessIncomeExtraExpenseLimitInputDisabled,
		value: params.buildingLimitsCommercial?.businessIncomeExtraExpense ?? 0,
		minValue: 0,
		minValueInclusive: true,
		showValidation: showValidation,
	});

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return buildingLimitInput.isDisabled
			&& contentsLimitInput.isDisabled
			&& businessIncomeExtraExpenseLimitInput.isDisabled;
	}, [
		buildingLimitInput.isDisabled,
		contentsLimitInput.isDisabled,
		businessIncomeExtraExpenseLimitInput.isDisabled,
	]);

	const showLimits: boolean = React.useMemo(() => {
		return showBuildingLimitInput
			|| showContentsLimitInput
			|| showBusinessIncomeExtraExpenseLimitInput;
	}, [
		showBuildingLimitInput,
		showContentsLimitInput,
		showBusinessIncomeExtraExpenseLimitInput,
	]);
	// #endregion variables

	return {
		areAllInputsDisabled,
		buildingLimitInput,
		contentsLimitInput,
		businessIncomeExtraExpenseLimitInput,
		applySubformBuildingLimitsProps,
		createBuildingLimitsCommercial,
		flagAllInputsToShowValidations,
		doInputsHaveChanges,
	};
}

// Export parameters.
export {
	UseSubformBuildingLimitsParameters,
}

// Export results.
export {
	UseSubformBuildingLimitsResults,
}

export default useSubformBuildingLimits;